import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Image as ImageIcon,
  Face as FaceIcon,
  Web as WebIcon,
} from '@mui/icons-material';

const SERVICES = [
  {
    name: 'Backend API',
    key: 'backend',
    url: 'https://api.bibahobangla.com/health',
    icon: CloudIcon,
    color: '#1976d2',
    description: 'Main API server handling all business logic',
  },
  {
    name: 'Static Website',
    key: 'static',
    url: 'https://bibahobangla.com/health',
    icon: WebIcon,
    color: '#2e7d32',
    description: 'SEO optimized public website',
  },
  {
    name: 'CDN Service',
    key: 'cdn',
    url: 'https://cdn.bibahobangla.com/health',
    icon: ImageIcon,
    color: '#ed6c02',
    description: 'Media storage and delivery service',
  },
  {
    name: 'Face Verify',
    key: 'faceverify',
    url: 'https://verify.bibahobangla.com/health',
    icon: FaceIcon,
    color: '#9c27b0',
    description: 'Face recognition and verification service',
  },
];

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatUptime = (seconds) => {
  if (!seconds) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const StatusChip = ({ status }) => {
  const config = {
    healthy: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Healthy' },
    degraded: { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Degraded' },
    error: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Error' },
    unknown: { color: 'default', icon: null, label: 'Unknown' },
  };

  const { color, icon, label } = config[status] || config.unknown;

  return (
    <Chip
      icon={icon}
      label={label}
      color={color}
      size="small"
      sx={{ fontWeight: 'bold' }}
    />
  );
};

const ServiceCard = ({ service, data, loading, error, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = service.icon;

  const getStatus = () => {
    if (error) return 'error';
    if (!data) return 'unknown';
    return data.status || 'unknown';
  };

  const status = getStatus();

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: status === 'healthy' ? 'success.light' : status === 'degraded' ? 'warning.light' : status === 'error' ? 'error.light' : 'grey.300',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
        }
      }}
    >
      {loading && <LinearProgress />}
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: service.color, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" component="div">
                {service.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {service.description}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <StatusChip status={status} />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => onRefresh(service.key)} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Response Time</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.responseTime ? `${data.responseTime}ms` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Uptime</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatUptime(data.uptime)}
                </Typography>
              </Grid>
              {data.memory && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Memory (RSS)</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatBytes(data.memory.rss)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Heap Used</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatBytes(data.memory.heapUsed)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>

            {data.checks && Object.keys(data.checks).length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => setExpanded(!expanded)}
                  sx={{ cursor: 'pointer', py: 1 }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Dependency Checks
                  </Typography>
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                <Collapse in={expanded}>
                  <Box sx={{ mt: 1 }}>
                    {Object.entries(data.checks).map(([key, check]) => (
                      <Box
                        key={key}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          py: 0.5,
                          px: 1,
                          mb: 0.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <StorageIcon fontSize="small" color="action" />
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {key}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {check.latency && (
                            <Typography variant="caption" color="text.secondary">
                              {check.latency}ms
                            </Typography>
                          )}
                          <Chip
                            size="small"
                            label={check.status}
                            color={check.status === 'connected' || check.status === 'healthy' ? 'success' : 'error'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const HealthCheck = () => {
  const [healthData, setHealthData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealth = useCallback(async (serviceKey) => {
    const service = SERVICES.find(s => s.key === serviceKey);
    if (!service) return;

    setLoading(prev => ({ ...prev, [serviceKey]: true }));
    setErrors(prev => ({ ...prev, [serviceKey]: null }));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(service.url, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setHealthData(prev => ({ ...prev, [serviceKey]: data }));
    } catch (err) {
      const errorMessage = err.name === 'AbortError'
        ? 'Request timeout (10s)'
        : err.message || 'Failed to connect';
      setErrors(prev => ({ ...prev, [serviceKey]: errorMessage }));
      setHealthData(prev => ({ ...prev, [serviceKey]: null }));
    } finally {
      setLoading(prev => ({ ...prev, [serviceKey]: false }));
    }
  }, []);

  const fetchAllHealth = useCallback(async () => {
    await Promise.all(SERVICES.map(s => fetchHealth(s.key)));
    setLastUpdated(new Date());
  }, [fetchHealth]);

  useEffect(() => {
    fetchAllHealth();
  }, [fetchAllHealth]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchAllHealth();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllHealth]);

  const overallStatus = () => {
    const statuses = SERVICES.map(s => {
      if (errors[s.key]) return 'error';
      if (!healthData[s.key]) return 'unknown';
      return healthData[s.key].status || 'unknown';
    });

    if (statuses.every(s => s === 'healthy')) return 'healthy';
    if (statuses.some(s => s === 'error')) return 'error';
    if (statuses.some(s => s === 'degraded')) return 'degraded';
    return 'unknown';
  };

  const healthyCount = SERVICES.filter(s =>
    healthData[s.key]?.status === 'healthy' && !errors[s.key]
  ).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            System Health
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor the health status of all BibahoBangla services
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Box textAlign="right">
            <Typography variant="body2" color="text.secondary">
              Last updated
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {lastUpdated ? lastUpdated.toLocaleTimeString("en-US", { timeZone: "Asia/Dhaka" }) : 'Never'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAllHealth}
            disabled={Object.values(loading).some(l => l)}
          >
            Refresh All
          </Button>
        </Box>
      </Box>

      {/* Overall Status */}
      <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <StatusChip status={overallStatus()} />
              <Typography variant="h6">
                Overall System Status
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {healthyCount} / {SERVICES.length} services healthy
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Service Cards */}
      <Grid container spacing={3}>
        {SERVICES.map(service => (
          <Grid item xs={12} md={6} key={service.key}>
            <ServiceCard
              service={service}
              data={healthData[service.key]}
              loading={loading[service.key]}
              error={errors[service.key]}
              onRefresh={fetchHealth}
            />
          </Grid>
        ))}
      </Grid>

      {/* Auto-refresh indicator */}
      <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={3}>
        <Chip
          size="small"
          label={autoRefresh ? 'Auto-refresh: ON (30s)' : 'Auto-refresh: OFF'}
          color={autoRefresh ? 'primary' : 'default'}
          onClick={() => setAutoRefresh(!autoRefresh)}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};

export default HealthCheck;
